"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Icon } from "@/lib/icons/Icon";
import {
  fetchProcedureGate,
  type ProcedureGate,
} from "@/lib/api/c-procedures-user";
import { CProceduresView } from "./plugins/CProcedures/CProceduresView";
import styles from "./ProcedureGate.module.css";

/**
 * Bramka procedur dla shellu konta (c_user_accounts) - port logiki panelu IADMIN
 * (`checkRequiredProcedures` + `getProcedureMessage`) do react-cms.
 *
 * Zachowanie (parytet legacy):
 *   - **Procedury w terminie** (niezaakceptowane, ale termin nie minal) -> banner
 *     ostrzegawczy nad kontem z liczba dni + link do zakladki procedur. User dalej
 *     korzysta z konta.
 *   - **Procedury po terminie** (`block=true`) -> TWARDA BLOKADA: zamiast konta
 *     renderujemy ekran z lista procedur do akceptacji (CProceduresView) + wyloguj.
 *     Po zaakceptowaniu wszystkich (onDecision -> refetch gate) blokada znika.
 *   - Brak procedur / blad -> przezroczyste (renderuje children).
 *
 * Fail-soft: blad sieci NIE blokuje konta (fetchProcedureGate zwraca block=false).
 * Podczas ladowania renderuje children (bez mignięcia blokady przed znanym stanem).
 */
export function ProcedureGuard({
  children,
  onLogout,
  logoutBusy,
}: {
  children: ReactNode;
  onLogout: () => void;
  logoutBusy?: boolean;
}) {
  const [gate, setGate] = useState<ProcedureGate | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    const g = await fetchProcedureGate();
    setGate(g);
    setLoaded(true);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /* Ladowanie / brak danych -> przepuszczamy konto (bez blokady-flash). */
  if (!loaded || !gate) return <>{children}</>;

  /* ── Twarda blokada (procedury po terminie) ── */
  if (gate.block) {
    const overdue = gate.pending.filter((p) => p.overdue);
    return (
      <div className={styles.block}>
        <div className={styles.blockHead}>
          <Icon name="warning-circle" className={styles.blockIcon} />
          <div className={styles.blockHeadBody}>
            <h2 className={styles.blockTitle}>Wymagana akceptacja procedur</h2>
            <p className={styles.blockText}>
              Aby kontynuować korzystanie z konta, musisz zaakceptować poniższe procedury.
              Termin akceptacji minął.
            </p>
            {overdue.length > 0 && (
              <ul className={styles.blockList}>
                {overdue.map((p, i) => (
                  <li key={i}>{p.name}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className={styles.blockProcedures}>
          <CProceduresView onDecision={() => void refresh()} />
        </div>

        <div className={styles.blockFooter}>
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={onLogout}
            disabled={logoutBusy}
          >
            <Icon name="lock" />
            Wyloguj się
          </button>
        </div>
      </div>
    );
  }

  /* ── Banner (procedury w terminie) ── */
  const hasPending = gate.pending.length > 0;
  return (
    <>
      {hasPending && (
        <div className={styles.banner} role="status">
          <Icon name="warning" className={styles.bannerIcon} />
          <div className={styles.bannerBody}>
            <span className={styles.bannerTitle}>
              Masz procedury oczekujące na akceptację
            </span>
            <ul className={styles.bannerList}>
              {gate.pending.map((p, i) => (
                <li key={i}>
                  {p.name}
                  {p.days_left > 0 ? ` - pozostało ${p.days_left} dni` : ""}
                </li>
              ))}
            </ul>
            <a className={styles.bannerLink} href="#extension-get-procedures">
              <Icon name="file-text" />
              Przejdź do akceptacji
            </a>
          </div>
        </div>
      )}
      {children}
    </>
  );
}
